import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import Seo from "@/components/site/Seo";
import PageHero from "@/components/site/PageHero";
import { CTABanner } from "@/components/site/Blocks";
import { PostCard } from "@/components/home/Journey";
import { http } from "@/lib/api";

const FILTERS = [["", "All"], ["country", "Country"], ["service", "Service"], ["policy", "Policy Updates"]];

export function Insights() {
  const [cat, setCat] = useState("");
  const [posts, setPosts] = useState(null);
  useEffect(() => {
    setPosts(null);
    http.get("/posts", { params: cat ? { category: cat } : {} }).then((r) => setPosts(Array.isArray(r.data) ? r.data : [])).catch(() => setPosts([]));
  }, [cat]);
  return (
    <>
      <Seo title="Insights" description="Considered perspectives on visas, residence, investment migration and policy updates across the UK, Australia, New Zealand, Canada, the USA and Europe." />
      <PageHero compact eyebrow="Insights" lines={["Perspective from", "the private desk."]} image="/img/paris.jpg" testid="insights-hero" />
      <section className="section-lux bg-ivory">
        <div className="container-lux">
          <div className="flex flex-wrap gap-3 border-b border-navy/15 pb-8" role="tablist">
            {FILTERS.map(([v, l]) => (
              <button key={l} type="button" role="tab" aria-selected={cat === v} onClick={() => setCat(v)} data-testid={`insights-filter-${l.toLowerCase().replace(/\s/g, "-")}`} className={`border px-5 py-2.5 font-sans text-[12px] font-semibold uppercase tracking-[0.2em] transition-colors duration-500 ${cat === v ? "border-navy bg-navy text-ivory" : "border-navy/20 text-navy hover:border-navy"}`}>{l}</button>
            ))}
          </div>
          {posts === null ? <p className="py-20 text-charcoal-soft">Loading insights…</p> : posts.length === 0 ? <p className="py-20 text-charcoal-soft" data-testid="insights-empty">No articles in this category yet.</p> : (
            <div className="mt-14 grid gap-14 md:grid-cols-2 lg:grid-cols-3 lg:gap-x-8" data-testid="insights-grid">
              {posts.map((p, i) => <PostCard key={p.slug} p={p} i={i} />)}
            </div>
          )}
        </div>
      </section>
    </>
  );
}

const Body = ({ text }) => (
  <div className="prose-lux">
    {(text || "").split(/\n\n+/).map((b, i) => (b.startsWith("## ") ? <h2 key={i}>{b.slice(3)}</h2> : <p key={i}>{b}</p>))}
  </div>
);

export function Article() {
  const { slug } = useParams();
  const [p, setP] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    setP(null); setErr(false);
    http.get(`/posts/${slug}`).then((r) => setP(r.data)).catch(() => setErr(true));
  }, [slug]);
  if (err) return <div className="bg-navy pb-32 pt-48 text-center text-ivory"><p className="font-serif text-4xl">Article not found.</p><Link to="/insights" className="btn-gold mt-10"><span>Back to insights</span></Link></div>;
  if (!p) return <div className="min-h-screen bg-navy" />;
  return (
    <>
      <Seo title={p.title} description={p.excerpt} />
      <PageHero compact eyebrow={`${p.tag || p.category} · ${p.read_time} min read`} lines={[p.title]} image={p.cover} testid="article-hero" />
      <article className="section-lux bg-ivory" data-testid="article-body">
        <div className="container-lux grid gap-12 lg:grid-cols-[220px_1fr]">
          <aside className="text-[13px] text-charcoal-soft lg:sticky lg:top-32 lg:self-start">
            <Link to="/insights" className="link-lux font-semibold uppercase tracking-[0.2em] text-navy" data-testid="article-back-link"><ArrowLeft className="h-4 w-4" />Insights</Link>
            <p className="mt-6">{new Date(p.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}</p>
          </aside>
          <div className="max-w-3xl">
            <p className="mb-10 font-serif text-2xl leading-relaxed text-navy md:text-3xl">{p.excerpt}</p>
            <Body text={p.body} />
            <p className="mt-12 border-t border-navy/15 pt-6 text-[13px] text-charcoal-soft">This article is for general information only and does not constitute legal or immigration advice. Rules change frequently; please verify current requirements with official sources or speak with an adviser.</p>
          </div>
        </div>
      </article>
      <CTABanner />
    </>
  );
}
